"use client";

import { useState } from "react";

import type { PromptItem, PromptStatus } from "@/lib/api/types";
import { formatCount, formatDateTime } from "@/lib/format";
import { FUNNEL_LABEL, STATUS_LABEL, barTone, brandCoverage, brandRows, ownBrandLabel, scoreTone } from "@/lib/prompt-module";

type Props = {
  item: PromptItem;
  busy: boolean;
  error: string | null;
  runs: number;
  onDecide: (status: PromptStatus) => void;
  /** Speichert einen neuen Text. Wirft bei einem Fehler. */
  onEdit: (text: string) => Promise<void>;
  onValidate: () => void;
  validationAvailable: boolean;
};

/** Detailbereich (Mockup): Quelle, Validierung, Trigger-Test, Hinweis und Entscheidung. Bleibt beim Scrollen im Bild. */
export function PromptDetail({ item, busy, error, runs, onDecide, onEdit, onValidate, validationAvailable }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const tone = scoreTone(item.score);

  const save = async (text: string) => {
    setLocalError(null);
    try {
      await onEdit(text);
      setEditing(null);
    } catch (caught) {
      setLocalError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
    }
  };

  return (
    <div
      role="region"
      aria-label="Detail zum gewählten Prompt"
      className="flex min-w-0 flex-col gap-4 self-start px-[22px] py-[18px] lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto"
    >
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted">Quelle: {item.source_ref ?? item.source_label}</span>
        {editing === null ? (
          <p className="text-[17px] font-semibold leading-snug">{item.text}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <label htmlFor="prompt-edit" className="text-[13px] font-medium">
              Prompt bearbeiten
            </label>
            <textarea
              id="prompt-edit"
              value={editing}
              onChange={(event) => setEditing(event.target.value)}
              rows={3}
              className="rounded-md border border-field bg-white px-3 py-2 text-sm"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy || editing.trim().length === 0}
                onClick={() => void save(editing)}
                className="h-11 rounded-md bg-accent px-3.5 text-sm font-medium text-white disabled:opacity-60"
              >
                Speichern
              </button>
              <button type="button" onClick={() => setEditing(null)} className="h-11 rounded-md border border-field bg-white px-3.5 text-sm">
                Abbrechen
              </button>
            </div>
            <p className="text-xs text-muted">Ein neuer Text löscht Score und Trigger-Test, danach erneut validieren.</p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className={`rounded px-2 py-0.5 font-mono text-[13px] font-semibold ${tone.className}`}>
            {item.score === null ? "kein Score" : `Score ${item.score} · ${tone.label}`}
          </span>
          <span className="rounded bg-neutral-bg px-2 py-0.5 text-xs font-medium text-[#3a424a]">{STATUS_LABEL[item.status]}</span>
          {item.tag && <span className="rounded bg-neutral-bg px-2 py-0.5 text-xs font-medium text-[#3a424a]">Cluster {item.tag}</span>}
          {item.funnel && <span className="text-xs text-muted">{FUNNEL_LABEL[item.funnel]}</span>}
          {item.kind && <span className="text-xs text-muted">{item.kind}</span>}
          {item.volume !== null && (
            <span className="text-xs text-muted">
              Suchvolumen {formatCount(item.volume)}
              {item.volume_ref ? ` · ${item.volume_ref}` : ""}
            </span>
          )}
          {item.duplicates > 0 && <span className="text-xs text-muted">{formatCount(item.duplicates)} Duplikate zusammengeführt</span>}
        </div>
      </div>

      <section aria-labelledby="validation-title" className="flex flex-col gap-2">
        <h3 id="validation-title" className="text-[13px] font-semibold">
          Validierung
        </h3>
        <ul className="flex flex-col gap-2">
          {item.checks.map((check) => (
            <li key={check.key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_44px] items-center gap-3 text-[13px]">
              <span>{check.label}</span>
              <div
                role="img"
                aria-label={`${check.label}: ${check.value === null ? "nicht ermittelt" : `${check.value} von 100`}`}
                className="h-2 overflow-hidden rounded bg-[#e9ecea]"
              >
                {check.value !== null && <div className={`h-full ${barTone(check.value)}`} style={{ width: `${check.value}%` }} />}
              </div>
              <span className="text-right font-mono text-xs">{check.value === null ? "–" : check.value}</span>
            </li>
          ))}
        </ul>
        {item.missing.length > 0 && (
          <p className="text-xs text-muted">
            Nicht ermittelt: {item.checks.filter((c) => item.missing.includes(c.key)).map((c) => c.label).join(", ")}. Der Score rechnet
            ohne diese Teile.
            {item.missing.includes("demand") && " Über „Nachfrage“ oben lässt sich das Suchvolumen über das Parent Topic nachholen."}
          </p>
        )}
        {item.hint && <p className="text-sm leading-relaxed">{item.hint}</p>}
        {item.better && editing === null && (
          <div className="flex flex-col gap-2 rounded-md border border-[#ccdaec] bg-[#f2f6fb] p-3">
            <span className="text-xs font-semibold text-accent">Vorschlag für eine natürlichere Formulierung</span>
            <span className="text-sm">{item.better}</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void save(item.better ?? "")}
              className="h-11 w-fit rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
            >
              Vorschlag übernehmen
            </button>
          </div>
        )}
        {!item.validated && validationAvailable && (
          <button
            type="button"
            disabled={busy}
            onClick={onValidate}
            className="h-11 w-fit rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
          >
            Jetzt validieren
          </button>
        )}
      </section>

      <section aria-labelledby="trigger-title" className="flex flex-col gap-2">
        <h3 id="trigger-title" className="text-[13px] font-semibold">
          Trigger-Test · {item.trigger?.runs ?? runs} Läufe je Plattform
        </h3>
        {item.trigger ? (
          <>
            <table aria-label="Trigger-Test" className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="py-1 font-medium">Plattform</th>
                  <th className="py-1 font-medium">Websuche</th>
                  <th className="py-1 font-medium">Quellen Ø</th>
                  <th className="py-1 font-medium">Eigene Domain</th>
                  <th className="py-1 font-medium">Marke genannt</th>
                </tr>
              </thead>
              <tbody>
                {item.trigger.platforms.map((p) => (
                  <tr key={p.key} className="border-t border-neutral-bg">
                    <th scope="row" className="py-1.5 text-left font-medium">
                      {p.label}
                    </th>
                    <td className="py-1.5 font-mono">
                      {p.web}/{p.runs}
                      {p.failed > 0 ? ` (${p.failed} fehlgeschlagen)` : ""}
                    </td>
                    <td className="py-1.5 font-mono">{p.sources_avg === null ? "–" : String(p.sources_avg).replace(".", ",")}</td>
                    <td className="py-1.5">{p.own > 0 ? `ja (${p.own}/${p.runs})` : "nein"}</td>
                    <td className="py-1.5">{ownBrandLabel(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <FailedRuns trigger={item.trigger} />
            <BrandList trigger={item.trigger} />
            {item.trigger.summary && <p className="text-sm leading-relaxed">{item.trigger.summary}</p>}
            {item.trigger.at && <p className="text-xs text-muted">Getestet am {formatDateTime(item.trigger.at)}</p>}
          </>
        ) : (
          <p className="text-sm text-muted">Noch nicht getestet. Mit dem Trigger-Test siehst du, ob der Prompt eine Websuche auslöst und die eigene Domain zitiert wird.</p>
        )}
      </section>

      {(error ?? localError) && (
        <p role="alert" className="text-[13px] text-danger">
          {error ?? localError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {item.status !== "freigegeben" && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide("freigegeben")}
            className="h-11 rounded-md bg-accent px-3.5 text-sm font-medium text-white disabled:opacity-60"
          >
            Freigeben
          </button>
        )}
        <button
          type="button"
          disabled={busy || editing !== null}
          onClick={() => setEditing(item.text)}
          className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
        >
          Bearbeiten
        </button>
        {item.status !== "verworfen" && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide("verworfen")}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm text-danger disabled:opacity-60"
          >
            Verwerfen
          </button>
        )}
        {item.status !== "vorschlag" && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide("vorschlag")}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
          >
            Zurück zu Vorschlägen
          </button>
        )}
      </div>
    </div>
  );
}

/** Welche Marken in den Antworten vorkamen (Zahl der Läufe, Aufteilung je Plattform). */
function BrandList({ trigger }: { trigger: NonNullable<PromptItem["trigger"]> }) {
  const coverage = brandCoverage(trigger);
  if (coverage === "none") {
    return <p className="text-xs text-muted">Marken sind in diesem Test nicht erfasst. Mit einem neuen Trigger-Test erscheinen sie hier.</p>;
  }
  if (coverage === "own") {
    return <p className="text-xs text-muted">Andere Marken wurden nicht erkannt (Gemini war nicht verfügbar), geprüft ist nur deine Marke.</p>;
  }
  const rows = brandRows(trigger);
  if (rows.length === 0) {
    return <p className="text-sm text-muted">In den Antworten wurden keine anderen Marken genannt.</p>;
  }
  return (
    <div className="flex flex-col gap-1">
      <h4 className="text-xs font-medium text-muted">Andere genannte Marken (Läufe)</h4>
      <ul aria-label="Genannte Marken" className="flex flex-col gap-0.5 text-[13px]">
        {rows.map((row) => (
          <li key={row.name} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">{row.name}</span>
            <span className="font-mono">{row.count}</span>
            <span className="text-xs text-muted">{row.detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Läufe, die keine Antwort geliefert haben, mit dem Grund. Sie zählen nicht mit, der Test misst dann weniger Läufe. */
function FailedRuns({ trigger }: { trigger: NonNullable<PromptItem["trigger"]> }) {
  const failed = trigger.platforms.filter((p) => p.failed > 0);
  if (failed.length === 0) return null;
  return (
    <ul aria-label="Fehlgeschlagene Läufe" className="flex flex-col gap-1 rounded-md bg-warning-bg px-3 py-2 text-[13px] text-warning">
      {failed.map((p) => (
        <li key={p.key}>
          <span className="font-medium">
            {p.label}: {p.runs === 0 ? "alle Läufe" : `${p.failed} von ${p.failed + p.runs} Läufen`} fehlgeschlagen.
          </span>{" "}
          {p.errors.length > 0
            ? p.errors.map((e) => (p.errors.length > 1 ? `${e.message} (${e.runs}×)` : e.message)).join("; ")
            : "Der Grund ist nicht bekannt."}{" "}
          Ein neuer Trigger-Test wiederholt die Läufe.
        </li>
      ))}
    </ul>
  );
}
